/**
 * Settings → Help: every page's "About this page" text in one place, as a
 * guide to the whole platform — an overview, then each page's description
 * and tasks grouped by area, and the same search the ⓘ dialogs use across
 * all of it.
 *
 * Pages the reader cannot open, and tasks their permissions rule out, are
 * left out (the same gates as the dialogs). Building pages need a building
 * to link to; outside one, the guide links to the building the reader last
 * opened, if any.
 */
import { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { PageInfoAreaId, PageInfoId } from "@/lib/i18n/types/pageInfo";
import { useStores } from "@/stores/useStores";
import { PAGE_INFO, PAGE_INFO_AREAS } from "./pageInfoRegistry";
import { PageInfoSearchBox, SearchResults, TaskItem } from "./PageInfoTaskList";
import { useVisibleChunks, type PageInfoScope } from "./visibleChunks";
import type { PageInfoChunk } from "./search/pageInfoChunks";
import { usePageInfoSearch } from "./search/usePageInfoSearch";

function positiveInt(raw: string | null | undefined): number | null {
  const id = raw ? Number.parseInt(raw, 10) : NaN;
  return Number.isFinite(id) && id > 0 ? id : null;
}

export const PlatformGuide = observer(function PlatformGuide() {
  const { t, language } = useLanguage();
  const g = t.pageInfo.guide;
  const { buildingStore } = useStores();
  const lastBuildingId = buildingStore.lastVisitedBuildingId;
  const scope = useMemo<PageInfoScope>(
    () => ({ buildingId: positiveInt(lastBuildingId), projectId: null }),
    [lastBuildingId]
  );
  const chunks = useVisibleChunks(scope, null);
  const [query, setQuery] = useState("");
  const search = usePageInfoSearch(query, chunks, language);

  const byArea = useMemo(() => {
    const pages = new Map<PageInfoAreaId, Array<{ page: PageInfoId; chunks: PageInfoChunk[] }>>();
    const seen = new Map<PageInfoId, PageInfoChunk[]>();
    for (const chunk of chunks) {
      const list = seen.get(chunk.page) ?? [];
      list.push(chunk);
      seen.set(chunk.page, list);
    }
    for (const [page, pageChunks] of seen) {
      const area = PAGE_INFO[page].area;
      if (!area) continue;
      const list = pages.get(area) ?? [];
      list.push({ page, chunks: pageChunks });
      pages.set(area, list);
    }
    return pages;
  }, [chunks]);

  return (
    <div className="space-y-8">
      <PageInfoSearchBox query={query} onQueryChange={setQuery} />

      {search.query ? (
        <SearchResults key={search.query} page={null} scope={scope} search={search} />
      ) : (
        <>
          <section className="space-y-3 rounded-lg bg-muted/30 p-5">
            <h2 className="text-base font-semibold">{g.overviewTitle}</h2>
            {g.overview.map((paragraph) => (
              <p key={paragraph} className="text-sm leading-relaxed text-muted-foreground">
                {paragraph}
              </p>
            ))}
          </section>

          {PAGE_INFO_AREAS.map((area) => {
            const pages = byArea.get(area);
            if (!pages?.length) return null;
            return (
              <section key={area} aria-labelledby={`guide-area-${area}`} className="space-y-3">
                <h2 id={`guide-area-${area}`} className="text-lg font-semibold">
                  {g.areas[area]}
                </h2>
                <div className="space-y-3">
                  {pages.map(({ page, chunks: pageChunks }) => (
                    <PageGuideCard key={page} page={page} chunks={pageChunks} scope={scope} />
                  ))}
                </div>
              </section>
            );
          })}
        </>
      )}
    </div>
  );
});

function PageGuideCard({
  page,
  chunks,
  scope,
}: {
  page: PageInfoId;
  chunks: PageInfoChunk[];
  scope: PageInfoScope;
}) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const def = PAGE_INFO[page];
  const title = (def.searchTitle ?? def.title)(t);
  const route = def.route(scope);

  return (
    <article className="rounded-lg border p-5" aria-labelledby={`guide-page-${page}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h3 id={`guide-page-${page}`} className="text-base font-semibold">
          {title}
        </h3>
        {route ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => navigate(route)}
          >
            {fill(t.pageInfo.guide.openPage, { page: title })}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">{t.pageInfo.search.openBuildingFirst}</p>
        )}
      </div>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        {t.pageInfo.pages[page].intro}
      </p>
      <Accordion type="single" collapsible className="mt-2">
        <AccordionItem value="tasks" className="border-b-0">
          <AccordionTrigger className="py-2 text-sm font-medium">
            {fill(t.pageInfo.guide.showTasks, { count: String(chunks.length) })}
          </AccordionTrigger>
          <AccordionContent>
            <Accordion type="single" collapsible>
              {chunks.map((chunk) => (
                <TaskItem key={chunk.key} chunk={chunk} page={null} scope={scope} inPageCard />
              ))}
            </Accordion>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </article>
  );
}
